import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../axiosInstance";

import AuthContext from "../AuthContext";
import {
  FaUsers,
  FaPlayCircle,
  FaDollarSign,
  FaCheckCircle,
  FaShieldAlt,
  FaPhoneAlt,
  FaArrowRight,
  FaTasks,
  FaMobileAlt,
  FaBolt,
  FaCut,
  FaStar,
  FaClinicMedical,
  FaShoppingCart,
  FaHeadset,
  FaClock,
  FaBuilding,
} from "react-icons/fa";
import { motion } from "framer-motion";
import mainpic from "../images/MainPic.PNG";
import checkinTablet from "../images/tablet-customer.png";
import taskScreenImg from "../images/TaskScreen.png";
import customerQueueScreenImg from "../images/CustomerQueueScreen.png";
import tabletStandImg from "../images/TabletStand.jpg";
import taskTabletImg from "../images/TaskTablet.webp";
import tabletImg from "../images/tablet.png";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const sectionHeading = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

// Everyday businesses that manage a walk-in queue or a team task list — the
// two things this app actually does. Kept honest to real functionality.
const USE_CASES = [
  {
    icon: <FaCut className="text-2xl" />,
    title: "Barber Shops",
    desc: "Walk-ins check themselves in and get a number — no more shouting names across the shop.",
    color: "from-blue-500 to-blue-600",
  },
  {
    icon: <FaStar className="text-2xl" />,
    title: "Hair & Nail Salons",
    desc: "Manage several stylists and walk-in clients from one tablet at reception.",
    color: "from-pink-500 to-pink-600",
  },
  {
    icon: <FaClinicMedical className="text-2xl" />,
    title: "Clinics & Surgeries",
    desc: "A clear, calm waiting-room queue instead of a shared notebook or shouted names.",
    color: "from-green-500 to-green-600",
  },
  {
    icon: <FaShoppingCart className="text-2xl" />,
    title: "Retail & Service Counters",
    desc: "Deli counters, repair shops, post offices — anywhere customers wait their turn.",
    color: "from-orange-500 to-orange-600",
  },
  {
    icon: <FaTasks className="text-2xl" />,
    title: "Small Teams",
    desc: "Switch to Task Mode and run your team's day from a High / Medium / Low priority list.",
    color: "from-purple-500 to-purple-600",
  },
  {
    icon: <FaHeadset className="text-2xl" />,
    title: "Support & Help Desks",
    desc: "Triage incoming requests by priority so nothing urgent gets buried.",
    color: "from-teal-500 to-teal-600",
  },
];

const STATS = [
  {
    icon: <FaBuilding className="text-2xl" />,
    value: "1,000+",
    label: "Businesses Trust CustomerQueue",
  },
  {
    icon: <FaUsers className="text-2xl" />,
    value: "18,000+",
    label: "Customer Check-Ins Every Day",
  },
  {
    icon: <FaShieldAlt className="text-2xl" />,
    value: "99.9%",
    label: "Uptime You Can Rely On",
  },
];

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn } = useContext(AuthContext);
  const [showButton, setShowButton] = useState(false);

  const handleSignUpClick = () => navigate("/register");

  useEffect(() => {
    const handleScroll = () => setShowButton(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Heartbeat check to backend
    const checkHeartbeat = async () => {
      try {
        const response = await axiosInstance.get("/api/heartbeat/heartbeat");
        console.log("Heartbeat response:", response.data);
      } catch (error) {
        console.error("Heartbeat check failed:", error);
      }
    };

    checkHeartbeat();
  }, []);

  // Smooth-scroll to a section when the URL contains a hash (e.g. coming from
  // the navbar's "Features" / "Pricing" links on another page).
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      const timer = setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <div className="bg-white overflow-x-hidden">
      {/* ══════════════════════════ HERO ══════════════════════════ */}
      <section
        id="hero"
        className="relative scroll-mt-20 bg-gradient-to-b from-blue-50 via-white to-white overflow-hidden"
      >
        {/* Decorative background blobs */}
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 bg-blue-200/40 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 w-72 h-72 bg-purple-200/40 rounded-full blur-3xl" />

        <div className="relative container mx-auto px-6 pt-14 pb-20 lg:pt-20 lg:pb-28 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Copy */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.6 }}
            className="text-center lg:text-left"
          >
            <span className="inline-block bg-blue-100 text-blue-700 text-xs font-bold px-4 py-1.5 rounded-full mb-5 uppercase tracking-wide">
              Queue &amp; Task Management, Simplified
            </span>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 leading-tight mb-5">
              Simple Customer Queue Management &amp;{" "}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Simple Task Manager.
              </span>
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0">
              CustomerQueue lets walk-in customers check themselves in while
              you manage the live queue — or switch to Task Mode to keep your
              team focused on what matters most. One screen, one device,
              ready in minutes — for just{" "}
              <span className="font-semibold text-green-600">$5/month</span>.
            </p>
            <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4 mb-8">
              {!isLoggedIn ? (
                <>
                  <motion.button
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                    onClick={handleSignUpClick}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-7 py-3.5 rounded-xl font-bold shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2"
                  >
                    Get Started <FaArrowRight />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                    onClick={() => navigate("/demo-queue")}
                    className="bg-green-600 hover:bg-green-700 text-white px-7 py-3.5 rounded-xl font-semibold shadow-lg shadow-green-600/20 flex items-center justify-center gap-2"
                  >
                    <FaPlayCircle /> See Live Demo
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                    onClick={() => {
                      document
                        .getElementById("how-it-works")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="bg-white border-2 border-gray-200 text-gray-700 hover:border-gray-300 px-7 py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2"
                  >
                    How to Setup
                  </motion.button>
                </>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  onClick={() => navigate("/queue")}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-7 py-3.5 rounded-xl font-bold shadow-lg shadow-blue-600/20"
                >
                  Go to Your Queue
                </motion.button>
              )}
            </div>
            <p className="text-sm text-gray-500 flex items-center justify-center lg:justify-start gap-2">
              <FaCheckCircle className="text-green-500" /> Just $5/month
              <span className="text-gray-300">•</span>
              <FaCheckCircle className="text-green-500" /> No contracts
              <span className="text-gray-300">•</span>
              <FaCheckCircle className="text-green-500" /> Cancel anytime
            </p>
          </motion.div>

          {/* Product visual with floating callout cards */}
          <motion.div
            className="relative mx-auto max-w-md lg:max-w-full"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <div className="relative">
              <img
                src={mainpic}
                alt="CustomerQueue running on a tablet at a barber shop counter"
                className="rounded-2xl shadow-2xl w-full border border-gray-100"
              />

              {/* Floating callout — top */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="hidden sm:flex absolute -top-6 -right-4 sm:-right-8 bg-white rounded-xl shadow-xl px-4 py-3 items-center gap-3 border border-gray-100"
              >
                <span className="bg-blue-100 text-blue-600 w-9 h-9 rounded-full flex items-center justify-center">
                  <FaUsers />
                </span>
                <div>
                  <p className="text-xs text-gray-400 leading-none mb-1">
                    Now Waiting
                  </p>
                  <p className="text-sm font-bold text-gray-800 leading-none">
                    4 customers
                  </p>
                </div>
              </motion.div>

              {/* Floating callout — bottom */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="hidden sm:flex absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-xl shadow-xl px-4 py-3 items-center gap-3 border border-gray-100"
              >
                <span className="bg-red-100 text-red-600 w-9 h-9 rounded-full flex items-center justify-center">
                  <FaTasks />
                </span>
                <div>
                  <p className="text-xs text-gray-400 leading-none mb-1">
                    Top Task
                  </p>
                  <p className="text-sm font-bold text-gray-800 leading-none">
                    🔴 High Priority
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════ TRUST STATS ══════════════════════════ */}
      <section className="relative bg-gradient-to-r from-blue-600 to-purple-600 py-12 sm:py-14">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {STATS.map((stat, idx) => (
              <motion.div
                key={stat.label}
                className="text-center text-white"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
              >
                <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-white/15 flex items-center justify-center">
                  {stat.icon}
                </div>
                <p className="text-3xl sm:text-4xl font-extrabold leading-tight">
                  {stat.value}
                </p>
                <p className="text-sm sm:text-base text-blue-100 mt-1">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════ PRODUCT SHOWCASE ══════════════════════════ */}
      <section id="product" className="scroll-mt-20 py-16 sm:py-20 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <motion.div
            className="text-center max-w-2xl mx-auto mb-12"
            variants={sectionHeading}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              One App. Two Powerful Modes.
            </h2>
            <p className="text-gray-500 text-lg">
              See it for yourself — switch between managing a live customer
              queue and running a priority task list, instantly, on the same
              screen.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              className="group bg-gray-50 rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-shadow border border-gray-100"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="overflow-hidden">
                <img
                  src={customerQueueScreenImg}
                  alt="Customer Queue Mode showing customer check-in and live queue list"
                  className="w-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-blue-600 bg-blue-100 px-3 py-1 rounded-full mb-3">
                  👥 Customer Queue Mode
                </span>
                <h3 className="text-xl font-bold text-gray-800 mb-1">
                  Let Customers Check Themselves In
                </h3>
                <p className="text-gray-500 text-sm">
                  Customers enter their name and instantly get a queue number
                  — no paper tickets, no receptionist required.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="group bg-gray-50 rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-shadow border border-gray-100"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="overflow-hidden">
                <img
                  src={taskScreenImg}
                  alt="Task Queue Mode showing priority task list with high medium low priority badges"
                  className="w-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-purple-600 bg-purple-100 px-3 py-1 rounded-full mb-3">
                  📋 Task Queue Mode
                </span>
                <h3 className="text-xl font-bold text-gray-800 mb-1">
                  Keep Your Team on Track
                </h3>
                <p className="text-gray-500 text-sm">
                  Add tasks with High, Medium, or Low priority — the app
                  auto-sorts them so everyone works on what matters most.
                </p>
              </div>
            </motion.div>
          </div>

          <div className="text-center mt-10">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate("/demo-queue")}
              className="bg-green-600 hover:bg-green-700 text-white px-7 py-3.5 rounded-xl font-semibold shadow-lg shadow-green-600/20 inline-flex items-center gap-2"
            >
              <FaPlayCircle /> Try the Live Demo — No Sign-Up Needed
            </motion.button>
          </div>
        </div>
      </section>

      {/* ══════════════════════════ HOW IT HELPS YOU ══════════════════════════ */}
      <section
        id="features"
        className="scroll-mt-20 py-16 sm:py-20 bg-gray-50"
      >
        <div className="container mx-auto px-6 max-w-6xl">
          <motion.div
            className="text-center max-w-2xl mx-auto mb-12"
            variants={sectionHeading}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Less Waiting. Less Confusion.{" "}
              <span className="text-blue-600">More Organised Days.</span>
            </h2>
            <p className="text-gray-500 text-lg">
              CustomerQueue takes the everyday chaos out of running a
              walk-in business or a busy team.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: <FaUsers className="text-2xl text-blue-500" />,
                title: "Instant Self Check-In",
                desc: "Customers type their name and get a queue number in seconds — no paper, no waiting on staff.",
              },
              {
                icon: <FaCheckCircle className="text-2xl text-green-500" />,
                title: "Live Queue, Always in Sync",
                desc: "See who's waiting, serve the next customer, or remove someone with a single tap.",
              },
              {
                icon: <FaTasks className="text-2xl text-purple-500" />,
                title: "Priority Task Sorting",
                desc: "🔴 High priority tasks automatically bubble to the top so nothing urgent gets missed.",
              },
              {
                icon: <FaMobileAlt className="text-2xl text-orange-500" />,
                title: "Works on Any Device",
                desc: "A tablet, phone, or laptop is all you need. No special hardware or installs.",
              },
              {
                icon: <FaShieldAlt className="text-2xl text-emerald-500" />,
                title: "Your Data Stays on Your Device",
                desc: "Customer names and task details are stored locally — nothing leaves the counter.",
              },
              {
                icon: <FaDollarSign className="text-2xl text-yellow-500" />,
                title: "One Flat Price",
                desc: "$5/month, all features included. No contracts, no setup fees, no per-user charges.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-lg transition border border-gray-100"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center mb-4">
                  {item.icon}
                </div>
                <h3 className="font-bold text-gray-800 mb-1.5">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════ USE CASES ══════════════════════════ */}
      <section
        id="use-cases"
        className="scroll-mt-20 py-16 sm:py-20 bg-white"
      >
        <div className="container mx-auto px-6 max-w-6xl">
          <motion.div
            className="text-center max-w-2xl mx-auto mb-12"
            variants={sectionHeading}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Built for Businesses Like Yours
            </h2>
            <p className="text-gray-500 text-lg">
              Anywhere customers wait their turn, or a team needs to know
              what to tackle next.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {USE_CASES.map((item, i) => (
              <motion.div
                key={item.title}
                className="relative overflow-hidden rounded-2xl p-6 shadow-sm hover:shadow-xl transition bg-gray-50 border border-gray-100"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
              >
                <div
                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} text-white flex items-center justify-center mb-4 shadow-md`}
                >
                  {item.icon}
                </div>
                <h3 className="font-bold text-gray-800 mb-1.5">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════ 3-STEP ONBOARDING ══════════════════════════ */}
      <section
        id="how-it-works"
        className="scroll-mt-20 py-16 sm:py-20 bg-gradient-to-br from-blue-50 to-purple-50"
      >
        <div className="container mx-auto px-6 max-w-5xl">
          <motion.div
            className="text-center max-w-2xl mx-auto mb-12"
            variants={sectionHeading}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Up and Running in Minutes
            </h2>
            <p className="text-gray-500 text-lg">
              It's pretty easy to setup — only takes 5 minutes, max.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                color: "bg-blue-600",
                title: "Sign Up on Account",
                desc: "Create your account in under a minute — just your name, business, and email.",
              },
              {
                step: "2",
                color: "bg-green-600",
                title: "Use Any Tablet's Browser",
                image: tabletImg,
                imageAlt: "Any tablet works with CustomerQueue",
                desc: "iPad, Android, or Amazon Fire — grab any tablet you already own. No special hardware needed.",
              },
              {
                step: "3",
                color: "bg-purple-600",
                title: "Use Any Tablet Stand",
                image: tabletStandImg,
                imageAlt: "Any tablet stand works with CustomerQueue",
                desc: "Pop it on any stand you like to give it a permanent home at your counter.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                className="bg-white rounded-2xl shadow-md p-8 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.12 }}
              >
                <span
                  className={`${item.color} text-white text-2xl font-extrabold w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5 shadow`}
                >
                  {item.step}
                </span>
                {item.image && (
                  <img
                    src={item.image}
                    alt={item.imageAlt}
                    className="w-full h-32 object-contain mb-4"
                  />
                )}
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSignUpClick}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3.5 rounded-xl font-bold shadow-lg shadow-blue-600/20 inline-flex items-center gap-2"
            >
              Create Your Account <FaArrowRight />
            </motion.button>
          </div>
        </div>
      </section>

      {/* ══════════════════════════ THE ACTUAL EXPERIENCE ══════════════════════════ */}
      <section className="scroll-mt-20 py-16 sm:py-20 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="order-2 lg:order-1"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Everything You Need, in One Simple Place
              </h2>
              <p className="text-gray-600 mb-6">
                Once you're set up, your screen becomes mission control for
                your day — no spreadsheets, no sticky notes, no shouting
                across the room.
              </p>
              <ul className="space-y-4">
                {[
                  "Check in customers and serve them in the order they arrived",
                  "Switch instantly between Customer Queue Mode and Task Mode",
                  "Track how many customers you served, with a 7-day history view",
                  "Audio alerts when someone checks in or a task is removed",
                ].map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <FaCheckCircle className="text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-600">{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              className="order-1 lg:order-2"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <img
                src={taskTabletImg}
                alt="CustomerQueue task manager in use on a tablet"
                className="rounded-2xl shadow-2xl w-full border border-gray-100"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════ BENEFITS STRIP ══════════════════════════ */}
      <section className="py-14 bg-gray-900">
        <div className="container mx-auto px-6 max-w-6xl">
          <motion.h2
            className="text-2xl sm:text-3xl font-bold text-white text-center mb-10"
            variants={sectionHeading}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Why Businesses Switch to CustomerQueue
          </motion.h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-8">
            {[
              { icon: <FaClock />, text: "Save time every shift" },
              { icon: <FaUsers />, text: "Keep customers organised" },
              { icon: <FaBolt />, text: "Cut manual admin work" },
              { icon: <FaMobileAlt />, text: "Runs on any tablet, phone or laptop" },
              { icon: <FaCheckCircle />, text: "Simple, 2-minute setup" },
              { icon: <FaShieldAlt />, text: "Data stays on your device" },
              { icon: <FaTasks />, text: "Easy for any staff member" },
              { icon: <FaDollarSign />, text: "One flat, honest price" },
            ].map((b, i) => (
              <motion.div
                key={b.text}
                className="flex flex-col items-center text-center gap-2"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
              >
                <span className="text-blue-400 text-xl">{b.icon}</span>
                <span className="text-gray-300 text-sm font-medium">
                  {b.text}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════ PRICING / TRUST ══════════════════════════ */}
      <section id="pricing" className="scroll-mt-20 py-16 sm:py-20 bg-gray-50">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Pricing card */}
            <motion.div
              className="bg-white rounded-2xl shadow-lg p-8 sm:p-10 border-t-4 border-green-500"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Simple, Honest Pricing
              </h2>
              <p className="text-5xl font-extrabold text-green-600 mb-1">
                $5<span className="text-lg font-semibold">/month</span>
              </p>
              <p className="text-gray-400 mb-6">
                No setup fees · cancel any time
              </p>
              <ul className="space-y-3 mb-8 text-gray-600">
                {[
                  "Unlimited queue entries per day",
                  "Customer Queue Mode + Task Queue Mode",
                  "Works on any tablet, phone, or laptop",
                  "No contracts, no setup fees",
                ].map((point) => (
                  <li key={point} className="flex items-start gap-2">
                    <FaCheckCircle className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleSignUpClick}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3.5 rounded-xl shadow"
              >
                Get Started
              </motion.button>
            </motion.div>

            {/* Trust points */}
            <motion.div
              className="flex flex-col justify-center"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                Try It Before You Commit
              </h2>
              <div className="space-y-5">
                {[
                  {
                    title: "See the full app first",
                    desc: "Try the live demo — both Customer Queue Mode and Task Mode — with no sign-up required.",
                  },
                  {
                    title: "Your data, your device",
                    desc: "Customer and task information is stored locally, not shared with third parties.",
                  },
                  {
                    title: "No lock-in",
                    desc: "One flat monthly price, cancel whenever you like — no long-term contracts.",
                  },
                  {
                    title: "Real support, real people",
                    desc: (
                      <>
                        Questions before you sign up? Call{" "}
                        <a
                          href="tel:+61466127889"
                          className="text-blue-600 hover:underline font-medium"
                        >
                          +61 466 127 889
                        </a>
                        .
                      </>
                    ),
                  },
                ].map((t) => (
                  <div key={t.title} className="flex items-start gap-3">
                    <FaCheckCircle className="text-blue-500 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-800">
                        {t.title}
                      </p>
                      <p className="text-sm text-gray-500">{t.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate("/demo-queue")}
                className="mt-8 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                <FaPlayCircle /> Try the Live Demo
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════ HARDWARE (bring your own) ══════════════════════════ */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <motion.h2
            className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3 text-center"
            variants={sectionHeading}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            No New Hardware to Buy
          </motion.h2>
          <p className="text-center text-gray-500 mb-10">
            Just a device you probably already have.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              className="bg-gray-50 rounded-2xl p-6 text-center border border-gray-100"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <img
                src={checkinTablet}
                alt="Any tablet works with CustomerQueue"
                className="w-20 h-20 mx-auto mb-4 object-contain"
              />
              <h3 className="font-bold text-gray-800 mb-2">Any Tablet</h3>
              <p className="text-gray-500 text-sm">
                iPad, Android, or Amazon Fire — if it has a web browser, it
                works. Customers check in on one side, you manage the queue
                on the other.
              </p>
            </motion.div>
            <motion.div
              className="bg-gray-50 rounded-2xl p-6 text-center border border-gray-100"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <img
                src={tabletStandImg}
                alt="Any tablet stand works with CustomerQueue"
                className="w-20 h-20 mx-auto mb-4 object-contain"
              />
              <h3 className="font-bold text-gray-800 mb-2">
                Any Tablet Stand
              </h3>
              <p className="text-gray-500 text-sm">
                Give it a permanent home at the counter. Need help choosing
                one?{" "}
                <a
                  href="tel:+61466127889"
                  className="text-blue-600 hover:underline font-medium"
                >
                  <FaPhoneAlt className="inline mb-0.5" /> Call our team
                </a>
                .
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════ FINAL CTA ══════════════════════════ */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-center">
        <div className="container mx-auto px-6 max-w-2xl">
          <motion.div
            variants={sectionHeading}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">
              Ready to Make Your Day a Little Easier?
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Create your account, set it up in minutes, and start managing
              your customers or your team's tasks today.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSignUpClick}
              className="bg-white text-blue-700 hover:bg-blue-50 px-9 py-4 rounded-xl font-extrabold text-lg shadow-xl inline-flex items-center gap-2"
            >
              Start Using It Today <FaArrowRight />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm">
              © {new Date().getFullYear()} CustomerQueue
            </p>
            <p className="text-sm flex flex-col sm:flex-row items-center gap-1 sm:gap-4">
              <span>
                For any support, call{" "}
                <a
                  href="tel:+61466127889"
                  className="text-blue-400 hover:underline"
                >
                  +61 466 127 889
                </a>
              </span>
              <span>
                or email{" "}
                <a
                  href="mailto:sukhi@skilledmates.com.au"
                  className="text-blue-400 hover:underline"
                >
                  sukhi@skilledmates.com.au
                </a>
              </span>
            </p>
          </div>
        </div>
      </footer>

      {/* Scroll-to-top button */}
      {showButton && (
        <motion.button
          onClick={scrollToTop}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="fixed bottom-5 right-5 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition"
        >
          ↑
        </motion.button>
      )}
    </div>
  );
};

export default HomePage;

